import { resetCacheOnStaleVersion } from '~~/shared/utils/staleVersionCache'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import {
  EDITOR_ADD_ON_ACTIVE_VALUE,
  EDITOR_TICKET_TYPE,
  type EditorTicketType,
} from '../constants/options'
import { editorStorage } from '../utils/editorPersistence'
import { createLotteryStoreCache } from '../utils/editorStore'

export type EditorPrepaidSubscriptionAmount = {
  label: string
  value: number
}

export type EditorSelectedAddOn = {
  name: string
  value: string
}

export type EditorSavedOptions = {
  prepaidSubscriptionAmount: Maybe<EditorPrepaidSubscriptionAmount>
  selectedAddOns: EditorSelectedAddOn[]
  ticketType: EditorTicketType
}

const toggleableAddOns = (config: Config) => config.addOns.filter(({ perPlay }) => !perPlay)

const seedAddOns = (config: Config): EditorSelectedAddOn[] =>
  toggleableAddOns(config).map(({ mandatory, name, options }) => ({
    name,
    value: mandatory ? (options[0] ?? EDITOR_ADD_ON_ACTIVE_VALUE) : '',
  }))

const reconcileAddOns = (config: Config, selected: EditorSelectedAddOn[]) =>
  seedAddOns(config).map((addOn) => {
    const stored = selected.find(({ name }) => isSameAddOn(name, addOn.name))
    return stored ? { ...addOn, value: stored.value } : addOn
  })

const createStore = (lotteryId: LotteryId) =>
  defineStore(
    `ltc_lottery_options_${lotteryId.toLowerCase()}`,
    () => {
      const addOnRequirements = ref<AddOnRequirements>({})
      const configVersion = ref<Config['version']>('')
      const prepaidSubscriptionAmount = ref<Maybe<EditorPrepaidSubscriptionAmount>>(null)
      // options from an earlier session wait here until the customer restores or drops them
      const saved = ref<Maybe<EditorSavedOptions>>(null)
      const selectedAddOns = ref<EditorSelectedAddOn[]>([])
      const ticketType = ref<EditorTicketType>(EDITOR_TICKET_TYPE.Single)

      const getAddOnRequirement = (name: string) => getRequiredAddOn(addOnRequirements.value, name)

      const isAddOnDisabled = (name: string) =>
        isAddOnRestricted(addOnRequirements.value, selectedAddOns.value, name)

      const isAddOnSelected = (name: string) => hasSelectedAddOn(selectedAddOns.value, name)

      const reset = (config: Config) => {
        prepaidSubscriptionAmount.value = null
        selectedAddOns.value = sanitize(seedAddOns(config))
        ticketType.value = EDITOR_TICKET_TYPE.Single
      }

      const sanitize = (addOns: EditorSelectedAddOn[]) =>
        sanitizeSelectedAddOns(addOnRequirements.value, addOns)

      const stash = () => {
        saved.value = {
          prepaidSubscriptionAmount: prepaidSubscriptionAmount.value,
          selectedAddOns: selectedAddOns.value,
          ticketType: ticketType.value,
        }
        clearSelection()
      }

      const clearSelection = () => {
        prepaidSubscriptionAmount.value = null
        selectedAddOns.value = []
        ticketType.value = EDITOR_TICKET_TYPE.Single
      }

      const clearSaved = () => {
        saved.value = null
      }

      // the next setUp seeds the add-ons of the config again
      const clear = () => {
        clearSelection()
        clearSaved()
      }

      const restoreSaved = (config: Config) => {
        if (!saved.value) return
        prepaidSubscriptionAmount.value = saved.value.prepaidSubscriptionAmount
        selectedAddOns.value = sanitize(reconcileAddOns(config, saved.value.selectedAddOns))
        ticketType.value = saved.value.ticketType
        saved.value = null
      }

      const setAddOn = (name: string, isActive: boolean) => {
        if (!selectedAddOns.value.some((addOn) => isSameAddOn(addOn.name, name))) return

        selectedAddOns.value = sanitize(
          selectedAddOns.value.map((addOn) =>
            isSameAddOn(addOn.name, name)
              ? { ...addOn, value: isActive ? EDITOR_ADD_ON_ACTIVE_VALUE : '' }
              : addOn,
          ),
        )
      }

      const setPrepaidSubscriptionAmount = (amount: Maybe<EditorPrepaidSubscriptionAmount>) => {
        prepaidSubscriptionAmount.value = amount
      }

      const setTicketType = (type: EditorTicketType) => {
        if (type !== EDITOR_TICKET_TYPE.PrepaidSubscription) prepaidSubscriptionAmount.value = null
        ticketType.value = type
      }

      const hasSelection = () =>
        selectedAddOns.value.some(({ value }) => !!value) ||
        !!prepaidSubscriptionAmount.value ||
        ticketType.value !== EDITOR_TICKET_TYPE.Single ||
        !!saved.value

      const setUp = (config: Config) => {
        addOnRequirements.value = getAddOnRequirements(toggleableAddOns(config))

        const hasReset = resetCacheOnStaleVersion({
          cache: 'options',
          cachedVersion: configVersion.value,
          freshVersion: config.version,
          hasSelection,
          log: (type, payload) => useDatadog().log(type, payload),
          productId: lotteryId,
          reset: () => {
            reset(config)
            clearSaved()
          },
          syncVersion: (version) => (configVersion.value = version),
        })

        if (hasReset) return

        selectedAddOns.value = sanitize(reconcileAddOns(config, selectedAddOns.value))
      }

      return {
        clear,
        clearSaved,
        configVersion,
        getAddOnRequirement,
        isAddOnDisabled,
        isAddOnSelected,
        prepaidSubscriptionAmount,
        reset,
        restoreSaved,
        saved,
        selectedAddOns,
        setAddOn,
        setPrepaidSubscriptionAmount,
        setTicketType,
        setUp,
        stash,
        ticketType,
      }
    },
    {
      persist: {
        storage: editorStorage(),
      },
    },
  )

export const useEditorOptionsStore = createLotteryStoreCache(createStore)
